import { Feature } from 'ol';
import { get as getProjection, transformExtent, toLonLat } from 'ol/proj';
import Polygon from 'ol/geom/Polygon';
import { WKT, GeoJSON } from 'ol/format';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import {
    Icon, Style, Circle, Fill, Stroke, Text,
} from 'ol/style';
import { asArray } from 'ol/color';
import {
    Vector as VectorSource,
    TileWMS as TileWMSSource,
    WMTS as WMTSSource,
} from 'ol/source';
import {
    Heatmap as HeatmapLayer,
    Vector as VectorLayer,
    Tile as TileLayer,
} from 'ol/layer';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { getWidth, getTopLeft, getCenter } from 'ol/extent';
import arc from 'arc';
import Draw, { createBox } from 'ol/interaction/Draw';
import { getArea } from 'ol/sphere';
// import * as jsts from 'jsts';
import proj4 from './proj4reg';

/**
 * 地圖操作方法
 * @param {Object} gMap 地圖物件
 */
export default function MapMethod(gMap) {
    const gmap = gMap;
    const mapConfig = gMap._mapConfig;
    const map = gMap.olmap;
    const wktReader = new WKT();

    const methods = {
        /**
         * xy轉換坐標系統
         * @param {Array} coord xy
         * @param {String} epsgFrom
         * @param {String} epsgTo
         */
        convertCoord(coord, epsgFrom, epsgTo) {
            const rEpsgTo = epsgTo || mapConfig.options.projection;
            return proj4(epsgFrom, rEpsgTo, coord);
        },
        /**
         * WKT自動Fit
         * @param {String} wkt
         * @param {String} epsgFrom
         * @param {String} epsgTo
         */
        fitToWkt(wkt, epsgFrom, epsgTo) {
            const feature = this.getFeatureFromWkt(wkt);

            let extent = feature.getGeometry().getExtent();

            if (epsgFrom && epsgTo) {
                extent = transformExtent(extent, epsgFrom, epsgTo);
            }
            this.fit(extent);
        },
        /**
         * WKT取得Feature
         * @param {String} wkt
         */
        getFeatureFromWkt(wkt) {
            const feature = wktReader.readFeature(wkt);
            return feature;
        },
        /**
         * Fit
         * @param {Array} extent
         */
        fit(extent, options) {
            const opt = {
                padding: [20, 20, 20, 20],
                maxZoom: 19,
            };
            map.getView().fit(extent, { opt, ...options });
        },
        /**
         * 新增地圖方法
         * @param {String} methodName 方法名稱
         * @param {Function} func 方法
         */
        addMethods(methodName, func) {
            this[methodName] = func;
        },
        /**
         * 圖層加入
         * @param {String} lyrName 圖層名稱
         * @param {Object} lyr 圖層物件
         */
        addLayer(lyrName, lyr) {
            map.addLayer(lyr);
            gmap.customLayers[lyrName] = lyr;
        },
        /**
         * 圖層移除
         * @param {String} lyrName 圖層名稱
         */
        removeLayer(lyrName) {
            gmap.customLayers[lyrName].setSource(
                new VectorSource({ features: [] }),
            );
            delete gmap.customLayers[lyrName];
        },
        /**
         * 圖層設定zIndex
         * @param {String} lyrName 圖層名稱
         * @param {Number} zIndex 圖層zIndex
         */
        setZIndexLayer(lyrName, zIndex) {
            gmap.customLayers[lyrName].setZIndex(zIndex);
        },
        /**
         * 圖層顯示切換
         * @param {Object} options 該圖層options
         */
        toggleLayer(options) {
            gmap.customLayers[options.lyrName].setVisible(options.isVisible);
        },
        /**
         * 回傳WMS圖層
         * @param {*} options 圖層Options
         * {String} url wms source url
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns WMS圖層
         */
        getWMSLayer(options) {
            const wmsLayer = new TileLayer({
                source: new TileWMSSource({
                    url: options.url,
                    params: { LAYERS: 'topp:states', TILED: true },
                    // extent: options.extent,
                    serverType: 'geoserver',
                    // Countries have transparency, so do not fade tiles:
                    transition: 0,
                }),
                visible: options.isVisible,
            });

            // 自動圖層Fit
            if (options.extent) methods.fit(options.extent);

            wmsLayer.setZIndex(options.zIndex);
            return wmsLayer;
        },
        /**
         * 回傳WMTS圖層
         * @param {Object} options 圖層Options
         * {String} url wmts source url
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns WMTS圖層
         */
        getWMTSLayer(options) {
            const projection = getProjection('EPSG:3857');
            const projectionExtent = projection.getExtent();
            const size = getWidth(projectionExtent) / 256;
            const resolutions = new Array(14);
            const matrixIds = new Array(14);
            for (let z = 0; z < 14; z += 1) {
                // generate resolutions and matrixIds arrays for this WMTS
                resolutions[z] = size / window.Math.pow(2, z);
                matrixIds[z] = z;
            }
            const wmtsLayer = new TileLayer({
                opacity: 0.7,
                source: new WMTSSource({
                    url: options.url,
                    layer: '0',
                    matrixSet: options.matrixSet,
                    format: 'image/png',
                    projection,
                    tileGrid: new WMTSTileGrid({
                        origin: getTopLeft(projectionExtent),
                        resolutions,
                        matrixIds,
                    }),
                    style: 'default',
                    wrapX: true,
                }),
                visible: options.isVisible,
            });

            // 自動圖層Fit
            if (options.extent) methods.fit(options.extent);

            wmtsLayer.setZIndex(options.zIndex);
            return wmtsLayer;
        },
        /**
         * 回傳WKT圖層
         * @param {String} wkt wkt資料
         * @param {Object} options 圖層Options
         * {String} projection 資料projection
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns WKT圖層
         */
        getWKTLayer(wkt, options) {
            const feature = wktReader.readFeature(wkt, {
                dataProjection: options.projection
                    ? options.projection
                    : 'EPSG:3826',
                featureProjection: mapConfig.options.projection,
            });

            feature.setStyle(
                new Style({
                    stroke: new Stroke({
                        color: '#17aa6f',
                        // lineDash: [4],
                        width: 3,
                    }),
                    fill: new Fill({
                        color: 'rgba(23, 170, 111, 0.2)',
                    }),
                }),
            );

            // 自動圖層Fit
            const extent = feature.getGeometry().getExtent();
            methods.fit(extent, {
                padding: [50, 50, 50, 50],
            });

            const wktLayer = new VectorLayer({
                source: new VectorSource({
                    features: [feature],
                }),
                visible: options.isVisible,
            });

            wktLayer.setZIndex(options.zIndex);
            return wktLayer;
        },
        /**
         * 回傳geoJSON圖層
         * @param {Array} fs 所有Features
         * @param {Object} options 圖層Options
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns GeoJSON圖層
         */
        getGeojsonLayer(fs, options) {
            const image = new Circle({
                radius: 5,
                fill: null,
                stroke: new Stroke({ color: 'red', width: 1 }),
            });

            const styles = {
                Point: new Style({
                    image,
                }),
                LineString: new Style({
                    stroke: new Stroke({
                        color: 'green',
                        width: 1,
                    }),
                }),
                MultiLineString: new Style({
                    stroke: new Stroke({
                        color: 'green',
                        width: 1,
                    }),
                }),
                MultiPoint: new Style({
                    image,
                }),
                MultiPolygon: new Style({
                    stroke: new Stroke({
                        color: 'yellow',
                        width: 1,
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 0, 0.1)',
                    }),
                }),
                Polygon: new Style({
                    stroke: new Stroke({
                        color: 'blue',
                        lineDash: [4],
                        width: 3,
                    }),
                    fill: new Fill({
                        color: 'rgba(0, 0, 255, 0.1)',
                    }),
                }),
                GeometryCollection: new Style({
                    stroke: new Stroke({
                        color: 'magenta',
                        width: 2,
                    }),
                    fill: new Fill({
                        color: 'magenta',
                    }),
                    image: new Circle({
                        radius: 10,
                        fill: null,
                        stroke: new Stroke({
                            color: 'magenta',
                        }),
                    }),
                }),
                Circle: new Style({
                    stroke: new Stroke({
                        color: 'red',
                        width: 2,
                    }),
                    fill: new Fill({
                        color: 'rgba(255,0,0,0.2)',
                    }),
                }),
            };

            const styleFunction = (feature) => styles[feature.getGeometry().getType()];

            const vectorSource = new VectorSource({
                features: fs,
            });

            // 自動圖層Fit
            const extent = vectorSource.getExtent();
            methods.fit(extent);

            const vectorLayer = new VectorLayer({
                source: vectorSource,
                visible: options.isVisible,
                style: styleFunction,
            });

            vectorLayer.setZIndex(options.zIndex);
            return vectorLayer;
        },
        /**
         * 回傳Icon圖層
         * @param {Array} fs 所有Features
         * @param {Object} options 圖層Options
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns Icon圖層
         */
        getIconLayer(fs, options) {
            fs.forEach((f) => {
                // 如果Feature沒有自帶styleFunction就使用預設
                let styleFunc;
                const selfStyle = f.get('style');

                if (selfStyle && !options.isShowName) {
                    styleFunc = selfStyle;
                } else {
                    const selfName = options.isShowName ? f.get('name') : '';
                    const iconUrl = f.get('iconUrl');
                    const src = iconUrl || null;
                    const anchor = iconUrl ? [0.5, 1] : null;
                    styleFunc = methods.iconStyle(src, anchor, selfName);
                }
                f.setStyle(styleFunc);
            });

            const vectorSource = new VectorSource({
                features: fs,
            });

            // 自動圖層Fit
            const extent = vectorSource.getExtent();

            methods.fit(extent, {
                padding: [100, 100, 100, 100],
            });

            const vectorLayer = new VectorLayer({
                source: vectorSource,
                visible: options.isVisible,
            });

            vectorLayer.setZIndex(options.zIndex);
            return vectorLayer;
        },
        getOverlayLayer(fs, options, callback) {
            fs.forEach((f) => {
                f.setStyle(
                    new Style({
                        image: new Circle({
                            radius: 0,
                            fill: new Fill({ color: '#fff' }),
                            stroke: new Stroke({
                                color: '#fff',
                                width: 1,
                            }),
                        }),
                    }),
                );
                if (callback) callback(f, options.isVisible);
            });

            const vectorSource = new VectorSource({
                features: fs,
            });

            // 自動圖層Fit
            const extent = vectorSource.getExtent();
            methods.fit(extent, {
                padding: [200, 50, 50, 50],
            });

            const vectorLayer = new VectorLayer({
                source: vectorSource,
                visible: options.isVisible,
            });

            vectorLayer.setZIndex(options.zIndex);
            return vectorLayer;
        },
        /**
         * 回傳Flight圖層
         * @param {Array} fs 所有Features
         * @param {Array} centerCoord 中心點
         * @param {Object} options 圖層Options
         * {String} color 圈圈顏色
         * {Boolean} isVisible 圖層顯示與否
         * {Boolean} isOriginal 是否動畫從中心開始
         * {Number} zIndex 圖層zIndex
         * @returns Flight圖層
         */
        getFlightLayer(fs, centerCoord, options) {
            const center = centerCoord || mapConfig.options.center;

            const vectorSource = new VectorSource({
                features: fs,
                loader: () => {
                    // eslint-disable-next-line
                    map.on('postcompose', animateFlights);
                },
            });

            const style = new Style({
                stroke: new Stroke({
                    color: '#424242',
                    width: 1,
                }),
            });

            function animateFlights(event) {
                const { vectorContext } = event;
                const { frameState } = event;

                vectorContext.setStyle(style);

                const features = vectorSource.getFeatures();

                for (let i = 0; i < features.length; i += 1) {
                    const feature = features[i];

                    if (!feature.get('finished') && feature.get('start')) {
                        // only draw the lines for which the animation has not finished yet
                        const coords = feature.getGeometry().getCoordinates();
                        const elapsedTime = frameState.time - feature.get('start');
                        const elapsedPoints = elapsedTime * 0.1;

                        if (elapsedPoints >= coords.length) {
                            feature.set('finished', true);
                        }

                        const maxIndex = Math.min(elapsedPoints, coords.length);
                        const currentLine = new LineString(
                            coords.slice(0, maxIndex),
                        );

                        // directly draw the line with the vector context
                        vectorContext.drawGeometry(currentLine);
                    }
                }
                // tell OpenLayers to continue the animation
                map.render();
            }

            function addLater(feature, timeout) {
                window.setTimeout(() => {
                    feature.set('start', new Date().getTime());
                    vectorSource.addFeature(feature);
                }, timeout);
            }

            fs.forEach((f, idx) => {
                const to = toLonLat(f.get('geometry').getCoordinates());
                const from = toLonLat(center);
                // create an arc circle between the two locations

                const arcGeneratorOpt1 = options.isOriginal
                    ? { x: from[0], y: from[1] }
                    : { x: to[0], y: to[1] };
                const arcGeneratorOpt2 = options.isOriginal
                    ? { x: to[0], y: to[1] }
                    : { x: from[0], y: from[1] };
                const arcGenerator = new arc.GreatCircle(
                    arcGeneratorOpt1,
                    arcGeneratorOpt2,
                );

                const arcLine = arcGenerator.Arc(100, { offset: 0.00001 });
                if (arcLine.geometries.length === 1) {
                    const line = new LineString(arcLine.geometries[0].coords);
                    line.transform('EPSG:4326', 'EPSG:3857');

                    const feature = new Feature({
                        geometry: line,
                        finished: false,
                    });
                    // add the feature with a delay so that the animation
                    // for all features does not start at the same time
                    addLater(feature, idx * 50);
                }
            });

            // 自動圖層Fit
            const extent = vectorSource.getExtent();
            methods.fit(extent, {
                padding: [15, 20, 150, 20],
            });

            methods.setFlightCircleStyle(fs, options.color);

            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: (feature) => {
                    // if the animation is still active for a feature, do not
                    // render the feature with the layer style
                    if (
                        feature.get('finished')
                        || feature.get('name') === 'A點'
                    ) {
                        return style;
                    }
                    return null;
                },
                visible: options.isVisible,
            });

            vectorLayer.setZIndex(options.zIndex);
            return vectorLayer;
        },
        /**
         * 回傳Heat圖層
         * @param {Array} fs 所有Features
         * @param {Object} options 圖層Options
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns Heat圖層
         */
        getHeatLayer(fs, options) {
            const vectorSource = new VectorSource();

            const heatmapLayer = new HeatmapLayer({
                source: vectorSource,
                blur: 20,
                radius: 25,
                visible: options.isVisible,
            });

            heatmapLayer.getSource().on('addfeature', (event) => {
                const magnitude = event.feature.get('weight');
                event.feature.set('weight', magnitude);
            });

            heatmapLayer.getSource().addFeatures(fs);

            const extent = heatmapLayer.getSource().getExtent();
            methods.fit(extent);

            heatmapLayer.setZIndex(options.zIndex);
            return heatmapLayer;
        },
        /**
         * 回傳網格圖層
         * @param {Array} fs 所有Features
         * @param {Object} options 圖層Options
         * {String} color 網格顏色
         * {Boolean} isVisible 圖層顯示與否
         * {Number} zIndex 圖層zIndex
         * @returns 網格圖層
         */
        getGridLayer(fs, options) {
            fs.forEach((f) => {
                f.setStyle(methods.gridStyle(options.color, f.get('weight')));
            });

            const vectorSource = new VectorSource({
                features: fs,
            });

            const gridmapLayer = new VectorLayer({
                source: vectorSource,
                visible: options.isVisible,
            });
            gridmapLayer.setZIndex(options.zIndex);
            return gridmapLayer;
        },
        /**
         * Icon點位樣式
         * @param {String} iconSrc Icon圖示路徑
         * @param {Array} iconAnchor Icon標示位置
         * @param {String} showText 點位文字內容
         * @returns icon點位樣式
         */
        iconStyle(iconSrc, iconAnchor, showText) {
            const src = typeof iconSrc === 'string'
                ? iconSrc
                : `${window.location.origin}/ic_map_marker_red.png`;
            const anchor = iconAnchor || [0.5, 1];

            return [
                new Style({
                    image: new Icon({
                        anchor,
                        src,
                        rotateWithView: true,
                        scale: 0.3,
                        anchorXUnits: 'fraction',
                        anchorYUnits: 'fraction',
                        opacity: 1,
                    }),
                    text: new Text({
                        font: '12px sans-serif',
                        text: showText,
                        fill: new Fill({ color: '#6691D1' }),
                        stroke: new Stroke({ color: '#ffffff', width: 5 }),
                        offsetY: 10,
                    }),
                }),
            ];
        },
        /**
         * Circle點位樣式
         * @param {String} color 點位顏色(hex)
         * @param {String} showText 點位文字內容
         * @param {String} textColor 文字顏色(hex)
         * @param {Number} textSize 文字尺寸
         * @returns circle點位樣式
         */
        circleStyle(option) {
            const cColor = option.color || '#EE3840';

            const cFColor = asArray(cColor);
            cFColor[3] = option.opacity || 0.6;

            const tColor = option.textColor || cColor;
            const tSize = option.textSize || 12;

            const radius = option.radius || 5;
            const offsetY = option.offsetY !== undefined ? option.offsetY : -15;

            const tSWidth = option.tSWidth !== undefined ? option.tSWidth : 5;

            return new Style({
                image: new Circle({
                    radius,
                    fill: new Fill({ color: cFColor }),
                    stroke: new Stroke({
                        color: cColor,
                        width: 1,
                    }),
                }),
                text: new Text({
                    font: `bold ${tSize}px Microsoft JhengHei`,
                    text: option.showText,
                    fill: new Fill({ color: tColor }),
                    stroke: new Stroke({ color: '#ffffff', width: tSWidth }),
                    offsetY,
                }),
            });
        },
        /**
         * 根據zoom設定OD圖circle style
         * @param {Array} fs features
         * @param {String} color 圈圈顏色
         * @param {Number} zoom map zoom
         */
        setFlightCircleStyle(fs, color, zoom) {
            const nZoom = zoom || map.getView().getZoom();
            const nFs = fs.filter((f) => f.getGeometry().getType() === 'Point');
            let sequenceFs = [];

            nFs.forEach((el) => {
                if (sequenceFs.indexOf(el.get('totalPeople')) === -1) {
                    sequenceFs.push(el.get('totalPeople'));
                }
            });
            sequenceFs = sequenceFs.sort((a, b) => a - b);

            // function sequenceIdx(people) {
            //     let index = 0;
            //     for (let i = 0; i < sequenceFs.length; i += 1) {
            //         if (sequenceFs[i] === people) {
            //             index = i;
            //             break;
            //         }
            //     }
            //     return index;
            // }

            function getRadius() {
                let m;
                if (nZoom > 18) {
                    m = nZoom / 4;
                } else if (nZoom > 14) {
                    m = nZoom / 6;
                } else if (nZoom > 12) {
                    m = nZoom / 8;
                } else {
                    m = nZoom / 14;
                }
                const cluster = {
                    minRadius: 30 * m,
                    maxRadius: 85 * m,
                };
                // const r = cluster.minRadius + (
                //     (cluster.maxRadius - cluster.minRadius)
                //     / sequenceFs.length * sequenceIdx(people));
                // 圈圈的大小改為一致
                const r = cluster.minRadius
                    + (cluster.maxRadius - cluster.minRadius) / sequenceFs.length;
                return r;
            }

            const maxP = Math.max(...nFs.map((f) => f.get('totalPeople')));

            nFs.forEach((f) => {
                const r = getRadius(f.get('totalPeople'));
                const tPeople = f.get('totalPeople');
                const name = f.get('name');
                // const tL = tPeople.toString().length;
                // const nL = name.toString().length;
                // const len = Math.max(tL, nL);
                // const fSize = Math.floor(r / len);
                const opacity = parseFloat(tPeople / (maxP * 1.4));
                const styleObj = {
                    color,
                    opacity,
                    showText: `${name}\n${tPeople}人`,
                    textColor: '#000',
                    textSize: 13, // fSize < 12 ? 12 : fSize,
                    tSWidth: 0,
                    radius: r,
                    offsetY: 1,
                };
                // 如果Feature沒有自帶styleFunction就使用預設
                const styleFunc = f.get('style') || methods.circleStyle(styleObj);

                f.setStyle(styleFunc);
            });
        },
        /**
         * 網格樣式
         * @param {String} color 網格顏色(hex)
         * @param {Number} weight 透明度
         */
        gridStyle(color, weight) {
            const colorArr = asArray(color);
            const nWeight = weight * 0.7;
            return new Style({
                stroke: new Stroke({
                    color,
                    width: 2,
                }),
                fill: new Fill({
                    color: [colorArr[0], colorArr[1], colorArr[2], nWeight],
                }),
            });
        },
        /**
         * 傳入geoJSON取得Feature
         * @param {Object} geojson geoJSON 資料物件
         * @returns Features
         */
        getGeojsonFeature(geojson, lyrOption) {
            const geojsonOpt = lyrOption.dataProjection
                ? {
                    dataProjection: lyrOption.dataProjection,
                    featureProjection: mapConfig.options.projection,
                }
                : {};
            const fs = new GeoJSON().readFeatures(geojson, geojsonOpt);
            return fs;
        },
        /**
         * 傳入資料取得icon型的Feature
         * @param {array} data 資料
         * @returns Features
         */
        getIconFeature(data) {
            const fs = data.map((f) => {
                const pos = [f.lng, f.lat];
                return new Feature({
                    geometry: new Point(pos),
                    name: f.title,
                    style: null,
                });
            });
            return fs;
        },
        /**
         * 地圖列印
         */
        printMap() {
            const m = document.getElementById(mapConfig.mapId);
            const canvas = m.getElementsByClassName('ol-unselectable')[0];
            const dpiFactor = 21 / canvas.width;

            function closePrint() {
                const h = m.classList.contains('geo-fullScreen') ? '100vh' : '';
                m.style.height = h;
            }
            document.querySelector('.geo-map').style.height = `${canvas.height
                * dpiFactor
                * 35}px`;
            window.onafterprint = closePrint;
            window.print();
        },
        /**
         * 圖面標繪
         * @param {String} type 形狀種類
         * @param {Function} callback 回呼方法
         */
        draw(type, callback) {
            gmap.drawSource = new VectorSource({ wrapX: false });
            const cColor = '#ee2314';
            const cFColor = asArray(cColor);
            cFColor[3] = 0.2;
            gmap.drawVector = new VectorLayer({
                source: gmap.drawSource,
                style: new Style({
                    fill: new Fill({
                        color: cFColor,
                    }),
                    stroke: new Stroke({
                        color: cColor,
                        width: 2,
                    }),
                    image: new Circle({
                        radius: 5,
                        fill: new Fill({
                            color: cFColor,
                        }),
                        stroke: new Stroke({
                            color: cColor,
                            width: 1,
                        }),
                    }),
                }),
            });

            // 先 hard code
            const geometryFunction = type === 'Circle' ? createBox() : '';

            const drawOpt = {
                type,
                source: gmap.drawSource,
                geometryFunction,
            };

            if (!gmap.customLayers.drawLayer) {
                methods.addLayer('drawLayer', gmap.drawVector);
            } else {
                gmap.olmap.removeInteraction(gmap.draw);
            }
            methods.addInteraction(drawOpt, callback);
        },
        /**
         * 新增標繪互動
         * @param {Object} drawOpt 標繪物件
         * @param {Function} callback 回呼方法
         */
        addInteraction(drawOpt, callback) {
            gmap.draw = new Draw(drawOpt);
            gmap.draw.on(
                'drawstart',
                () => {
                    if (gmap.customLayers.drawLayer) {
                        gmap.customLayers.drawLayer.getSource().clear();
                    }
                },
                this,
            );

            gmap.draw.on(
                'drawend',
                () => {
                    setTimeout(() => {
                        const fs = gmap.customLayers.drawLayer
                            .getSource()
                            .getFeatures();
                        const f = fs[0];
                        const geom = f.getGeometry();
                        const coordArr = geom.getCoordinates()[0];
                        const newCoordArr = coordArr.map((coord) => {
                            const newCoord = methods.convertCoord(
                                coord,
                                'EPSG:3826',
                                'EPSG:4326',
                            );
                            return newCoord;
                        });
                        const newGeom = new Polygon([newCoordArr]);
                        const ext = newGeom.getExtent();
                        const center = getCenter(ext);
                        const wkt = wktReader.writeGeometry(newGeom);
                        const area = getArea(geom);
                        callback(wkt, center, area);
                    }, 0);
                },
                this,
            );
            gmap.olmap.addInteraction(gmap.draw);
        },
        /**
         * 將WKT轉成valid
         * @param {String} wkt
         * @returns {String} nWkt
         */
        // makeWKTValid(wkt) {
        //     const writer = new jsts.io.WKTWriter();
        //     const reader = new jsts.io.WKTReader();
        //     const geom = reader.read(wkt);
        //     let nWkt = '';
        //     // 如果本身geom不合法就直接回傳空字串
        //     if (!geom.isValid()) {
        //         return nWkt;
        //     }
        //     const nGeom = geom.buffer(0);
        //     if (nGeom.isValid()) {
        //         nWkt = writer.write(nGeom);
        //     }
        //     return nWkt;
        // },
    };

    return methods;
}
